import { FC, ReactElement, useState } from "react";
import { differenceInMinutes, formatDistanceStrict } from "date-fns";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { TagValueType } from "./models/TagsModels";
import LightTooltipContent from "../../SolarGikLib/tooltip/LightTooltipContent";

import { RootState } from "../app/Store";
import { selectSiteTag, selectSiteTagDataStr } from "../app/store/MultisiteTagsStore";
import { invalidTagMetadata, getTagValueFormatter } from "../app/TagsMetadataUtils";
import { UserAccessType } from "../user/Models";
import { useInterval } from "../../common/Hooks/useInterval";
import { DAYJS_YEAR_TO_SECOND_FORMAT } from "../app/DayjsUtils";
import classes from "./DataPointTooltipContent.module.css";

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 60 * 24;
const MINUTES_TILL_DATE_IS_SHOWN = 60 * 12;
export const NO_DATA_FOR_TIME = "No data";

export function formatTimeForTooltip(sampleTime: Date, useTimeFormat: boolean) {
  const timeNow = new Date();
  if (sampleTime.getTime() <= 0) {
    return NO_DATA_FOR_TIME;
  }
  if (useTimeFormat) {
    return dayjs(sampleTime).format(DAYJS_YEAR_TO_SECOND_FORMAT);
  }
  if (Math.abs(differenceInMinutes(timeNow, sampleTime)) >= MINUTES_TILL_DATE_IS_SHOWN) {
    let timeDescription = MINUTES_TILL_DATE_IS_SHOWN + " minutes ago";
    if (
      MINUTES_TILL_DATE_IS_SHOWN > MINUTES_IN_DAY &&
      MINUTES_TILL_DATE_IS_SHOWN % MINUTES_IN_DAY === 0
    ) {
      timeDescription = MINUTES_TILL_DATE_IS_SHOWN / MINUTES_IN_DAY + " days ago";
    } else if (
      MINUTES_TILL_DATE_IS_SHOWN > MINUTES_IN_HOUR &&
      MINUTES_TILL_DATE_IS_SHOWN % MINUTES_IN_HOUR === 0
    ) {
      timeDescription = MINUTES_TILL_DATE_IS_SHOWN / MINUTES_IN_HOUR + " hours ago";
    }
    return "More then " + timeDescription;
  }
  return formatDistanceStrict(sampleTime, timeNow) + " ago";
}

export interface TooltipContentProps {
  tagName: string;
  siteId: string;
  isShowDateOnly: boolean;
  isShowTagMetadataOnly: boolean;
  displayName?: string;
  descriptionOverride?: string;
  valueToStringFuncOverride?: (value: number) => string;
  extraInfo?: ReactElement[];
}

const DataPointTooltipContent: FC<TooltipContentProps> = ({
  displayName = null,
  tagName,
  isShowDateOnly,
  isShowTagMetadataOnly,
  siteId,
  valueToStringFuncOverride,
  descriptionOverride,
  extraInfo,
}) => {
  const tagMetadata = useSelector(
    (state: RootState) => state.multiSitesMetadata.sites[siteId]?.tags[tagName] ?? invalidTagMetadata
  );
  const tagValuesStr = useSelector((state: RootState) => selectSiteTagDataStr(state, siteId, tagName));
  const userType = useSelector((state: RootState) => state.user.userType);
  const tag = useSelector((state: RootState) => selectSiteTag(state, siteId, tagName));
  const updateFormattedTime = () => formatTimeForTooltip(tagValuesStr.timestamp, false);
  const [formattedTime, setFormattedTime] = useState(updateFormattedTime);
  useInterval(() => setFormattedTime(updateFormattedTime()), 1000);

  let valueAsString: string;
  if (valueToStringFuncOverride != null) {
    valueAsString = valueToStringFuncOverride(tag.value);
  } else if (!tagMetadata.uniqueName) {
    valueAsString = tagValuesStr.value;
  } else {
    valueAsString = getTagValueFormatter(tagMetadata)(tag.value);
  }
  const showValueWithEnum = tagMetadata.tagValueType === TagValueType.Enum;

  if (displayName == null) {
    displayName = tagMetadata.displayName ?? tagName;
  }

  const hasUnit = tagMetadata.unitName != null && tagMetadata.unitName !== "";

  const isEngineer = userType >= UserAccessType.Engineer;

  const tooltipInfo: ReactElement[] = [];

  function addTagMetadataInfo() {
    tooltipInfo.push(<span>{descriptionOverride ?? tagMetadata.description}</span>);
    if (isEngineer) {
      tooltipInfo.push(<span>{`Tag: ${tagName}`}</span>);
      tooltipInfo.push(<span>{`Type: ${TagValueType[tagMetadata.tagValueType]}`}</span>);
    }
  }

  function addTagValueInfo() {
    if (isEngineer) {
      let valueInfo = "";
      if (showValueWithEnum) {
        valueInfo += "Value: ";
      }
      valueInfo += valueAsString;
      if (showValueWithEnum) {
        valueInfo += ` (${tag.value})`;
      }
      if (hasUnit) {
        valueInfo += ` (${tagMetadata.unitName})`;
      }
      tooltipInfo.push(<span>{valueInfo}</span>);
    }
    const formattedTimeToUse =
      formattedTime === NO_DATA_FOR_TIME
        ? NO_DATA_FOR_TIME
        : isEngineer
          ? formatTimeForTooltip(tagValuesStr.timestamp, true)
          : `Updated ${formattedTime}`;
    tooltipInfo.push(<span className={classes["tag-age-label"]}>{formattedTimeToUse}</span>);
  }

  if (!isShowDateOnly) {
    addTagMetadataInfo();
  }
  if (extraInfo) {
    tooltipInfo.push(...extraInfo);
  }
  if (!isShowTagMetadataOnly) {
    addTagValueInfo();
  }

  return <LightTooltipContent name={!isShowDateOnly ? displayName : undefined} info={tooltipInfo} />;
};

export default DataPointTooltipContent;
